import { useEffect, useRef } from 'react';
import axios from 'axios';

/**
 * Poll an invoice's financing state while a financing request waits for the customer.
 *
 * GET /api/invoices/:id/financing-status/ re-reads an open Resurs request from Resurs, so
 * the invoice view sees the customer finish even where the provider's callbacks cannot
 * reach the backend. Stops when ``active`` turns false, on unmount, or after ``maxMinutes``.
 * ``onUpdate`` gets the response body after every poll.
 */
export default function useFinancingStatus(invoiceId, active, onUpdate, { intervalMs = 15000, maxMinutes = 45 } = {}) {
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  useEffect(() => {
    if (!invoiceId || !active) return undefined;
    let cancelled = false;
    const started = Date.now();

    const poll = async () => {
      if (cancelled) return;
      if (Date.now() - started > maxMinutes * 60 * 1000) {
        clearInterval(timer);
        return;
      }
      try {
        const response = await axios.get(`/api/invoices/${invoiceId}/financing-status/`);
        if (!cancelled && onUpdateRef.current) onUpdateRef.current(response.data);
      } catch (err) {
        // A failed poll is retried on the next tick; the invoice view keeps showing the last state.
      }
    };

    const timer = setInterval(poll, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [invoiceId, active, intervalMs, maxMinutes]);
}
